// ═════════════════════════════════════════════════════════════════════════════
// Year 1 Plan — turning the live snapshot into what the screen renders (§6b).
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/yearOnePlan.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/yearOnePlan.js
//
// `diff` is the test; change one copy, change both in the same pass. Both people
// are choosing one shortlist together, so if the two apps disagree about which
// WIG sits in which section, what its number is, or who its owner is, they are
// arguing about different plans.
//
// Dependency-free, like `liveReviewAgenda.js` and `connectorsGraph.js`.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Two initials for an avatar.
 *
 * ⚠️ The server sends only `name` — there is no `initials` field, unlike the
 * mock this screen was first built against, which stored them precisely BECAUSE
 * deriving them is lossy. So this is a deliberate downgrade forced by the
 * contract, and it is written to fail gracefully rather than cleverly:
 *
 *   "Jane Doe"            → JD
 *   "Jane van der Berg"   → JB   (first + LAST token, so particles don't win)
 *   "Jane"                → JA   (two letters of the one token, not one letter
 *                                 in a circle sized for two)
 *   "" / null             → ?    (never an empty circle, which reads as broken)
 *
 * Uppercased at the end rather than per-branch so a lowercase name still yields
 * uppercase initials.
 */
export const initialsFor = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  const first = parts[0];
  if (parts.length === 1) {
    return (first.length > 1 ? first.slice(0, 2) : first).toUpperCase();
  }
  const last = parts[parts.length - 1];
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
};

/**
 * The screen's group tree, built from the live snapshot.
 *
 * Sections come from `sections` in `sequence` order — the same order the rest of
 * the session walked them — and each item keeps the four Year 1 fields the
 * socket store already defaulted onto it, so nothing here needs a guard.
 *
 * ⚠️ `priority` is DERIVED from position in the flattened list and is NOT an
 * identifier. The server sends no priority field at all; this is the design's
 * running number (Financial 1-3, Customer 4-6, …), so it is display shorthand
 * only — never persist it, never match on it, never send it anywhere.
 */
export const buildYearOneGroups = (snapshot) => {
  const sections = [...(snapshot?.sections || [])].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
  );

  let running = 0;
  return sections.map((section) => ({
    key: section.section_key,
    label: section.label || "",
    rows: (section.items || []).map((item) => {
      running += 1;
      return {
        id: item.execution_blueprint_item_id,
        priority: running,
        label: item.label || "",
        shortlisted: item.is_year1_shortlisted === true,
        // ── Owners ──
        // ⚠️ SINGULAR ON PURPOSE, FOR NOW (handoff #25 §4, 2026-09-14). An item
        // can carry several owners; the Year 1 row still shows one, and #25 is
        // explicit that the singular fields survive as "the first owner"
        // precisely so a screen can stay on them until it is ready.
        //
        // `owner_user_id`/`owner_name` are DERIVED from the list upstream (see
        // `normalizeOwners` in LiveReviewSocketStore), so these two are always
        // consistent with `ownerIds` below rather than a second, independently
        // read pair that could disagree with it.
        ownerId: item.owner_user_id ?? null,
        ownerName: item.owner_name || "",
        // The full set, carried through now so the row can grow into showing
        // several without another pass through this mapper. Nothing renders
        // these yet.
        ownerIds: item.owner_user_ids || [],
        owners: item.owners || [],
        value: item.value || "",
        // A styling hint only: the agent proposed this owner and nobody has
        // confirmed them. Any deliberate pick clears it server-side (§6b.6).
        ownerSuggested: item.owner_suggested_by_agent === true,
      };
    }),
  }));
};

/** How many WIGs are on the shortlist. The header chip's number. */
export const countShortlisted = (groups) =>
  (groups || []).reduce(
    (total, group) =>
      total + (group.rows || []).filter((row) => row.shortlisted).length,
    0,
  );

/**
 * True when the shortlist agent produced nothing.
 *
 * §6b.4: if the agent failed twice, every item comes back
 * `is_year1_shortlisted: false, owner_user_id: null` — and the handoff is
 * explicit that this is an EMPTY STATE, not an error. Both parties then build
 * the shortlist by hand, which is exactly what the screen already supports, so
 * the only thing this changes is the copy shown above the list.
 *
 * ⚠️ Deliberately distinguished from "no items at all": a blueprint with no
 * items is a different (and much stranger) situation than one whose agent
 * failed, and conflating them would tell the user the wrong thing.
 */
export const isShortlistEmpty = (groups) =>
  (groups || []).some((group) => (group.rows || []).length > 0) &&
  countShortlisted(groups) === 0;

/** The owner picker's options, from the snapshot's roster. */
export const buildRoster = (snapshot) =>
  (snapshot?.roster || []).map((member) => ({
    id: member.user_id,
    name: member.name || "",
    initials: initialsFor(member.name),
  }));
